import React, { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import * as z from "zod";
import { Button } from "../../ui/button";
import { toast } from "../../ui/use-toast";
import TextController from "../../form-fields/TextController";
import SelectController from "../../form-fields/SelectController";
import XanoAPI from "../../../lib/XanoAPI";

// Define schema for validation
const apiKeySchema = z.object({
  name: z.string().min(1, "API Key Name is required"),
  permissions: z.string(),
  key: z.string().optional(),
});

type ApiKeyValues = z.infer<typeof apiKeySchema> & {
  id?: string;
  created_at?: string;
  user_id?: number;
  user?: {
    id: number;
    name: string;
    email: string;
  };
};

// Mock data for API keys with members
const mockApiKeysWithMembers: Array<ApiKeyValues> = [
  {
    id: "key1",
    name: "Admin Key",
    key: "abcd-efgh-ijkl-1234",
    permissions: "read-write",
    created_at: "2025-01-28T10:00:00Z",
    user_id: 1,
    user: {
      id: 1,
      name: "Admin User",
      email: "admin@example.com",
    },
  },
  {
    id: "key2",
    name: "User Key - Jacob",
    key: "abcd-efgh-ijkl-5678",
    permissions: "read-only",
    created_at: "2025-01-28T11:00:00Z",
    user_id: 2,
    user: {
      id: 2,
      name: "Jacob",
      email: "jacob.hartmann@hartmannindustries.com",
    },
  },
  {
    id: "key3",
    name: "User Key - Bogdan",
    key: "abcd-efgh-ijkl-9101",
    permissions: "read-write",
    created_at: "2025-01-28T12:00:00Z",
    user_id: 4,
    user: {
      id: 4,
      name: "Bogdan M",
      email: "bogdan.martinescu@hartmannindustries.com",
    },
  },
];

export function ApiKeyManagement() {
  const api = XanoAPI.getInstance();
  const [apiKeys, setApiKeys] = useState<ApiKeyValues[]>(
    mockApiKeysWithMembers
  ); // Use mock data as default
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<ApiKeyValues>({
    resolver: zodResolver(apiKeySchema),
    defaultValues: {
      name: "",
      permissions: "read-only",
      key: "",
    },
    mode: "onChange",
  });

  const onSubmit = async (data: ApiKeyValues) => {
    try {
      setIsLoading(true);
      // Simulate creating an API key
      const newKey = {
        id: `key${apiKeys.length + 1}`,
        ...data,
        created_at: new Date().toISOString(),
        user: {
          id: api.getUserId(),
          name: "Current User",
          email: "current.user@example.com",
        },
      };
      setApiKeys((prevKeys) => [...prevKeys, newKey]);
      toast({
        title: "API Key Created",
        description: "Your new API key has been successfully created.",
      });
      form.reset();
    } catch (error) {
      console.error("Error creating API key:", error);
      toast({
        title: "Error",
        description: "Failed to create API key. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (keyId: string) => {
    try {
      setIsLoading(true);
      // Simulate deleting an API key
      setApiKeys((prevKeys) => prevKeys.filter((key) => key.id !== keyId));
      toast({
        title: "API Key Deleted",
        description: "The API key has been successfully deleted.",
      });
    } catch (error) {
      console.error("Error deleting API key:", error);
      toast({
        title: "Error",
        description: "Failed to delete API key. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-semibold text-accent">API Key Management</h2>

      {/* API Key Creation Form */}
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <TextController
            name="name"
            label="API Key Name"
            placeholder="Enter a name for your API key"
          />
          <SelectController
            name="permissions"
            label="Permissions"
            options={[
              { value: "read-only", label: "Read-Only" },
              { value: "read-write", label: "Read-Write" },
            ]}
            defaultValue="read-only"
          />
          <Button type="submit" className="bg-accent">
            Create API Key
          </Button>
        </form>
      </FormProvider>

      {/* API Key List */}
      <div className="space-y-6">
        <h3 className="text-xl font-semibold text-accent">Existing API Keys</h3>
        <div className="">
          {apiKeys.map((key) => (
            <div
              key={key.id}
              className="border rounded-lg p-4  bg-white space-y-2 mb-4"
            >
              <div className="flex justify-between">
                <p className="font-semibold text-lg text-accent">{key.name}</p>
                <span
                  className={`px-2 py-1 text-xs rounded ${
                    key.permissions === "read-write"
                      ? "bg-green-100 text-green-600"
                      : "bg-blue-100 text-blue-600"
                  }`}
                >
                  {key.permissions}
                </span>
              </div>
              <p className="text-sm text-gray-500">
                Key: <span className="font-mono">{key.key}</span>
              </p>
              <p className="text-sm text-gray-500">
                Created by: {key.user?.name} ({key.user?.email})
              </p>
              <p className="text-sm text-gray-500">
                Created at: {new Date(key.created_at || "").toLocaleString()}
              </p>
              <Button
                variant="outline"
                onClick={() => handleDelete(key.id || "")}
                className=" w-full"
              >
                Delete
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
